import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { useEnsureDraft } from '../lib/contentConfig';
import {
  TECH_NODE,
  buildTechGraph,
  reachability,
  recipeRequires,
  roomView,
  type RoomView,
  type TechGraph,
  type TechNode,
} from '../lib/s2/techGraph';
import type { Bundle } from '../lib/validate';

const STATUS_FILL: Record<string, string> = {
  facility: '#fde68a',
  workshop_tool: '#bfdbfe',
  key_part: '#fecaca',
  raw: '#e5e7eb',
  material: '#e5e7eb',
};

/**
 * The content tree (crafting.html §5 Admin): the column graph, a hand-built SVG of every item, recipe and facility tier by tier,
 * filtered by tier, with a click-through to the forms; the room preview per tier (slots and outlines); and the reachability report.
 */
export function TechTreePage() {
  const { draft, loading, error } = useEnsureDraft();
  const [tier, setTier] = useState<string>('all');
  const [picked, setPicked] = useState<string | null>(null);
  const b = draft?.bundle;
  const graph = useMemo(
    () => (b ? buildTechGraph(b, tier === 'all' ? undefined : Number(tier)) : null),
    [b, tier],
  );
  const allTiers = useMemo(() => (b ? buildTechGraph(b).tiers : []), [b]);
  const reach = useMemo(() => (b ? reachability(b) : null), [b]);
  if (error) return <p className="text-blam">Could not load content: {String(error)}</p>;
  if (loading || !b || !graph || !reach) return <p>Loading content&hellip;</p>;
  const node = graph.nodes.find((n) => n.key === picked);
  return (
    <>
      <PageHeader
        title="Content tree"
        description="Every item, recipe and facility as a graph, tier by tier (the designers' view; players see the room). Click a node for its recipes and links to the forms."
      >
        <label className="text-sm">
          Tier{' '}
          <select
            aria-label="Tier filter"
            className="rounded border-2 border-ink px-2 py-1"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
          >
            <option value="all">All tiers</option>
            {allTiers.map((t) => (
              <option key={t} value={t}>
                Tier {t}
              </option>
            ))}
          </select>
        </label>
      </PageHeader>
      <ReachabilitySummary unreachable={reach.unreachable} unbuildable={reach.unbuildable} />
      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <GraphView graph={graph} picked={picked} onPick={setPicked} />
        <NodeDetail bundle={b} node={node} />
      </div>
      <h2 className="mt-6 text-lg font-bold">Room preview</h2>
      <p className="text-xs text-bruise">
        Each tier&apos;s room with everything up to that tier built: solid stations, the next gate
        tool as a dashed outline, and the tool wall.
      </p>
      <div className="mt-2 grid gap-3 lg:grid-cols-2">
        {(allTiers.length ? allTiers : [0]).map((t) => (
          <RoomPreview key={t} view={roomView(b, t)} />
        ))}
      </div>
    </>
  );
}

/** The publish dialog's reachability report (crafting.html §5 Admin), once the draft has a tech tree. */
export function ReachabilityReport({ bundle }: { bundle: Bundle }) {
  const r = useMemo(() => reachability(bundle), [bundle]);
  if (!(bundle.techTiers ?? []).length) return null;
  return (
    <section className="mb-6 rounded border-2 border-ink bg-white p-4">
      <h2 className="font-semibold">Tech tree reachability</h2>
      <ReachabilitySummary unreachable={r.unreachable} unbuildable={r.unbuildable} />
    </section>
  );
}

function ReachabilitySummary({
  unreachable,
  unbuildable,
}: {
  unreachable: string[];
  unbuildable: string[];
}) {
  if (!unreachable.length && !unbuildable.length)
    return (
      <p className="mb-3 text-sm text-benday">
        Reachability: every item and facility can be reached from loot-table raws.
      </p>
    );
  return (
    <div className="mb-3 rounded border-2 border-blam/60 bg-white p-2 text-sm" role="status">
      {unreachable.length > 0 && (
        <p>
          <span className="font-semibold">Unreachable items ({unreachable.length}):</span>{' '}
          <span className="font-mono">{unreachable.join(', ')}</span>
        </p>
      )}
      {unbuildable.length > 0 && (
        <p>
          <span className="font-semibold">Facilities that can&apos;t be built:</span>{' '}
          <span className="font-mono">{unbuildable.join(', ')}</span>
        </p>
      )}
      <p className="text-xs text-bruise">
        Provisional walk until S2-TEC&apos;s validator lands: loot-table raws and key-part pieces,
        then recipes and facility costs.
      </p>
    </div>
  );
}

function GraphView({
  graph,
  picked,
  onPick,
}: {
  graph: TechGraph;
  picked: string | null;
  onPick: (k: string) => void;
}) {
  const byKey = new Map(graph.nodes.map((n) => [n.key, n]));
  const related = new Set<string>();
  if (picked)
    for (const e of graph.edges)
      if (e.to === picked || e.from === picked) {
        related.add(e.from);
        related.add(e.to);
      }
  return (
    <div className="max-h-[70vh] overflow-auto rounded border-2 border-ink bg-white">
      <svg width={graph.width} height={graph.height} role="img" aria-label="Content tree">
        {graph.tiers.map((t, i) => (
          <text key={t} x={10 + i * 210} y={18} fontSize={12} fontWeight="bold">
            Tier {t}
          </text>
        ))}
        {graph.edges.map((e, i) => {
          const a = byKey.get(e.from)!;
          const z = byKey.get(e.to)!;
          const x1 = a.x + TECH_NODE.w;
          const y1 = a.y + TECH_NODE.h / 2;
          const x2 = z.x;
          const y2 = z.y + TECH_NODE.h / 2;
          const hot = picked && (e.from === picked || e.to === picked);
          const back = x2 <= a.x;
          const d = back
            ? `M${a.x + TECH_NODE.w / 2},${a.y + TECH_NODE.h} C${a.x + TECH_NODE.w / 2},${y2 + 30} ${z.x + TECH_NODE.w / 2},${y2 + 30} ${z.x + TECH_NODE.w / 2},${z.y + TECH_NODE.h}`
            : `M${x1},${y1} C${x1 + 30},${y1} ${x2 - 30},${y2} ${x2},${y2}`;
          return (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={hot ? '#c0392b' : '#9ca3af'}
              strokeWidth={hot ? 1.6 : 0.7}
              strokeDasharray={e.kind === 'needs' ? '4 3' : undefined}
              opacity={picked && !hot ? 0.25 : 1}
            />
          );
        })}
        {graph.nodes.map((n) => (
          <g
            key={n.key}
            role="button"
            aria-label={`${n.kind} ${n.id}`}
            tabIndex={0}
            onClick={() => onPick(n.key)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') onPick(n.key);
            }}
            style={{ cursor: 'pointer' }}
            opacity={picked && picked !== n.key && !related.has(n.key) ? 0.35 : 1}
          >
            <rect
              x={n.x}
              y={n.y}
              width={TECH_NODE.w}
              height={TECH_NODE.h}
              rx={n.kind === 'facility' ? 0 : 4}
              fill={STATUS_FILL[n.category] ?? '#ffffff'}
              stroke={picked === n.key ? '#c0392b' : '#111827'}
              strokeWidth={picked === n.key ? 2 : 1}
            />
            <text x={n.x + 6} y={n.y + 15} fontSize={11}>
              {n.kind === 'facility' ? '▣ ' : n.madeBy.length ? '🔨 ' : '🔎 '}
              {n.label.length > 22 ? `${n.label.slice(0, 21)}…` : n.label}
            </text>
          </g>
        ))}
      </svg>
      <p className="px-2 py-1 text-xs text-bruise">
        ▣ facility · 🔨 made by a recipe · 🔎 must be found · solid line: input (used up) · dashed:
        needs (facility or tool).
      </p>
    </div>
  );
}

function NodeDetail({ bundle: b, node }: { bundle: Bundle; node?: TechNode }) {
  if (!node) return <p className="text-sm text-bruise">Pick a node in the tree.</p>;
  const recipes = (b.recipes ?? []).filter((r) => r && node.madeBy.includes(r.id ?? ''));
  const usedIn = (b.recipes ?? []).filter(
    (r) =>
      r &&
      ((r.inputs ?? []).some((i) => i.itemId === node.id) ||
        recipeRequires(r).some((q) => q.facility === node.id || q.tool === node.id)),
  );
  const module =
    node.kind === 'facility' ? (b.baseModules ?? []).find((m) => m?.id === node.id) : undefined;
  const sources = (b.lootTables ?? [])
    .filter((t) => (t?.entries ?? []).some((e) => e?.itemId === node.id))
    .map((t) => t.id);
  const formLink = (to: string, id: string) => (
    <Link to={to} search={{ id } as never} className="text-xs underline">
      open the form
    </Link>
  );
  return (
    <aside className="rounded border-2 border-ink bg-white p-3 text-sm" aria-label="Node detail">
      <h3 className="font-bold">
        {node.label} <span className="font-mono text-xs">({node.id})</span>
      </h3>
      <p className="text-xs">
        {node.kind === 'facility' ? 'Facility' : node.category} · tier {node.tier} ·{' '}
        {node.kind === 'facility'
          ? formLink('/content/modules', node.id)
          : formLink('/content/items', node.id)}
      </p>
      {module && (
        <ul className="mt-2 text-xs">
          {(module.levels ?? []).map((l, i) => (
            <li key={i}>
              Level {l.level}: tier {l.tier ?? 0}
              {l.unlocksTier ? ` · unlocks tier ${l.unlocksTier}` : ''}
              {l.roomSlot ? ` · slot ${l.roomSlot}` : ''} · cost{' '}
              {(l.cost ?? []).map((c) => `${c.quantity}× ${c.itemId}`).join(', ') || 'none'}
              {(l.requires ?? []).length
                ? ` · needs ${(l.requires ?? []).map(reqLabel).join(', ')}`
                : ''}
            </li>
          ))}
        </ul>
      )}
      {node.kind === 'item' && (
        <>
          <h4 className="mt-2 text-xs font-semibold uppercase">Made by</h4>
          {recipes.length === 0 && (
            <p className="text-xs">
              Found only{sources.length ? `: ${sources.join(', ')}` : ' (no loot table has it)'}.
            </p>
          )}
          {recipes.map((r) => (
            <div key={r.id} className="mt-1 rounded border border-newsprint p-1 text-xs">
              <p>
                <span className="font-mono">{r.id}</span> · tier {r.tier ?? 0} · {r.where || 'home'}
                {r.powerWh ? ` · ⚡ ${r.powerWh} Wh` : ''}
                {r.craftSeconds ? ` · ${r.craftSeconds} s` : ''} ·{' '}
                {formLink('/content/recipes', r.id!)}
              </p>
              <p>
                In:{' '}
                {(r.inputs ?? []).map((i) => `${i.quantity}× ${i.itemId}`).join(', ') || 'nothing'}
              </p>
              <p>Out: {(r.outputs ?? []).map((i) => `${i.quantity}× ${i.itemId}`).join(', ')}</p>
              {recipeRequires(r).length > 0 && (
                <p>Needs: {recipeRequires(r).map(reqLabel).join(', ')}</p>
              )}
            </div>
          ))}
        </>
      )}
      <h4 className="mt-2 text-xs font-semibold uppercase">Used in</h4>
      <p className="text-xs">{usedIn.map((r) => r.id).join(', ') || 'nothing'}</p>
    </aside>
  );
}

const reqLabel = (q: { facility?: string; level?: number; tool?: string; blueprint?: number }) =>
  q.facility
    ? `${q.facility}${q.level ? ` L${q.level + 1}` : ''}`
    : q.tool
      ? `tool ${q.tool}`
      : q.blueprint
        ? `blueprint T${q.blueprint}`
        : '?';

/** The room's 12 spots (crafting.html "The workshop room"): back row under the tool wall and the window, front row nearer. */
const SLOT_POS: Record<string, [number, number]> = {
  back_1: [20, 70],
  back_2: [120, 70],
  back_3: [220, 70],
  back_4: [320, 70],
  back_5: [420, 70],
  window: [520, 20],
  front_1: [20, 180],
  front_2: [110, 180],
  front_3: [200, 180],
  front_4: [290, 180],
  front_5: [380, 180],
  front_6: [470, 180],
};

export function RoomPreview({ view }: { view: RoomView }) {
  const shown = view.stations.filter((s) => s.state !== 'hidden');
  const problems = [
    ...view.conflicts.map((s) => `slot ${s} is shared`),
    ...view.unknownSlots.map((s) => `unknown slot ${s}`),
    ...view.unslotted.map((f) => `${f} has no room slot`),
  ];
  return (
    <figure
      className="rounded border-2 border-ink bg-white p-2"
      aria-label={`Room at tier ${view.tier}`}
    >
      <figcaption className="text-xs font-semibold">Tier {view.tier}</figcaption>
      <svg viewBox="0 0 600 240" className="w-full" role="img">
        <rect x={5} y={5} width={590} height={230} fill="#f5f0e6" stroke="#111827" />
        <rect
          x={110}
          y={12}
          width={200}
          height={40}
          fill="none"
          stroke="#111827"
          strokeDasharray="2 2"
        />
        <text x={115} y={24} fontSize={9}>
          Tool wall
        </text>
        {view.toolWall.map((t, i) => (
          <g key={t.itemId}>
            <circle
              cx={125 + i * 30}
              cy={38}
              r={8}
              fill={t.state === 'ink' ? '#111827' : 'none'}
              stroke="#111827"
              strokeDasharray={t.state === 'ink' ? undefined : '2 2'}
            />
            <title>{`${t.label} (${t.state === 'ink' ? 'owned' : 'outline'})`}</title>
          </g>
        ))}
        {Object.entries(SLOT_POS).map(([slot, [x, y]]) => (
          <text key={slot} x={x} y={y + 52} fontSize={8} fill="#6b7280">
            {slot}
          </text>
        ))}
        {shown.map((s) => {
          const pos = SLOT_POS[s.slot];
          if (!pos) return null;
          const conflict = view.conflicts.includes(s.slot);
          return (
            <g key={s.moduleId}>
              <rect
                x={pos[0]}
                y={pos[1]}
                width={80}
                height={40}
                fill={s.state === 'solid' ? '#fde68a' : 'none'}
                stroke={conflict ? '#c0392b' : '#111827'}
                strokeWidth={conflict ? 2 : 1}
                strokeDasharray={s.state === 'outline' ? '5 3' : undefined}
              />
              <text x={pos[0] + 4} y={pos[1] + 16} fontSize={9}>
                {s.label.slice(0, 14)}
              </text>
              <text x={pos[0] + 4} y={pos[1] + 30} fontSize={8} fill="#6b7280">
                T{s.tier}
                {s.gate ? ' gate' : ''}
              </text>
            </g>
          );
        })}
      </svg>
      {problems.length > 0 && <p className="text-xs text-blam">{problems.join(' · ')}</p>}
    </figure>
  );
}
